<template>
  <v-navigation-drawer
    v-model="useCartStore().isVisible"
    app
    location="right"
    width="420"
    :temporary="isMobile || !isScannerPage"
  >
    <v-toolbar density="compact" color="grey-darken-3" theme="dark">
      <v-toolbar-title>{{ $t("common.cart.cart") }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        v-feature="PlatformFeature.INVENTORY"
        v-if="!isScannerPage"
        variant="text"
        @click="redirectToScannerPage()"
        title="Scan"
      >
        <v-icon icon="mdi-qrcode" class="mr-2" />
        {{ $t("common.action.scan") }}
      </v-btn>
      <v-btn
        v-if="cartItems.length > 0"
        icon="mdi-delete-sweep"
        variant="text"
        @click="useCartStore().resetCart()"
        title="Clear Cart"
      ></v-btn>
    </v-toolbar>

    <v-list v-if="cartItems?.length > 0" class="pt-0">
      <v-slide-x-reverse-transition group tag="div">
        <v-list-item
          v-for="(item, index) in cartItems"
          :key="index"
          class="py-1"
          data-cy="cart-item"
          density="compact"
        >
          <v-divider v-if="index > 0" class="mb-2" />
          <template v-slot:prepend>
            <v-avatar size="40" :image="getCartItemImage(item)" :lazy-src="default_product_image" />
          </template>

          <v-row no-gutters class="w-100 px-1">
            <v-col cols="12">
              <v-row no-gutters align="start" class="ga-3">
                <v-col class="text-truncate">
                  <span class="text-subtitle-2 font-weight-medium text-truncate">
                    {{ getCartItemPrimaryLabel(item) }}
                  </span>
                  <v-row no-gutters class="ga-1 text-caption text-grey">
                    <v-col
                      v-if="item.productClassId && getCartItemProductName(item)"
                      cols="auto"
                      class="text-truncate"
                    >
                      {{ getCartItemProductName(item) }}
                    </v-col>
                    <v-col v-if="item.priceName" cols="auto" class="text-truncate ms-1">
                      {{ item.priceName || $t("common.cart.standardPrice") }}
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="auto" class="text-end">
                  <div>
                    <span class="font-weight-bold">{{
                      formatMoney({
                        amount: getNetLineTotal(item),
                        currency: item.currency,
                        hideCurrency: true,
                      })
                    }}</span>
                    <small class="text-grey ms-1">{{ item.currency.toUpperCase() }}</small>
                  </div>
                  <v-row no-gutters align="center" justify="end" class="text-caption text-grey">
                    <v-col cols="auto">{{
                      formatUnitPrice(getNetUnitPrice(item), item.currency)
                    }}</v-col>
                    <v-col cols="auto"
                      ><small class="ms-1">{{ $t("common.cart.eachAbbreviation") }}</small></v-col
                    >
                    <v-col v-if="itemHasDiscount(item)" cols="auto">
                      <del class="text-error text-caption">
                        <span>{{ formatUnitPrice(item.unitAmount, item.currency) }}</span>
                        <small class="ms-1">{{ $t("common.cart.eachAbbreviation") }}</small>
                      </del>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row no-gutters align="center" class="ga-3 mt-2">
                <v-col>
                  <v-chip
                    v-if="item.hasInventory !== false && cartStore.isItemOverOversellLimit(item)"
                    size="x-small"
                    color="error"
                    variant="tonal"
                    >{{ $t("common.cart.exceedsLimit", { limit: getOversellLimitLabel(item) })
                    }}{{ getLocationSuffix(item) }}</v-chip
                  >
                  <v-chip
                    v-else-if="
                      item.hasInventory !== false && cartStore.isItemInOversellTerritory(item)
                    "
                    size="x-small"
                    color="error"
                    variant="tonal"
                    >{{ $t("common.cart.oversellingTo", { count: getProjectedStockCount(item) })
                    }}{{ getLocationSuffix(item) }}</v-chip
                  >
                  <v-chip
                    v-else-if="
                      item.hasInventory !== false &&
                      getInventoryLowerBound(item) !== null &&
                      (item.stockCount || 0) <= getInventoryLowerBound(item)!
                    "
                    size="x-small"
                    color="warning"
                    variant="tonal"
                    >{{ $t("common.cart.atOversellLimit") }}{{ getLocationSuffix(item) }}</v-chip
                  >
                  <v-chip
                    v-else-if="item.hasInventory !== false && item.isLowStock"
                    size="x-small"
                    color="warning"
                    variant="tonal"
                    >{{ $t("common.cart.onlyStockLeft", { count: item.stockCount })
                    }}{{ getLocationSuffix(item) }}</v-chip
                  >
                  <v-chip
                    v-else-if="item.hasInventory !== false"
                    size="x-small"
                    color="success"
                    variant="tonal"
                    >{{ $t("common.cart.stockAvailable", { count: item.stockCount })
                    }}{{ getLocationSuffix(item) }}</v-chip
                  >
                  <v-chip v-else size="x-small" color="info" variant="tonal"
                    >{{ $t("common.cart.unlimited") }}{{ getLocationSuffix(item) }}</v-chip
                  >
                </v-col>

                <v-col cols="auto">
                  <v-chip
                    v-if="itemHasDiscount(item)"
                    color="blue"
                    variant="tonal"
                    size="small"
                    prepend-icon="mdi-tag-outline"
                    append-icon="mdi-pencil-outline"
                    @click="handleItemDiscountClick(item)"
                  >
                    <span v-if="item.discountType === 'percentage'">
                      -{{ item.discountTypeValue ?? 0 }}%
                    </span>
                    <i18n-t v-else keypath="common.cart.discountPerUnit" scope="global" tag="span">
                      <template #amount>
                        {{
                          formatMoney({
                            amount: item.discountAmount ?? 0,
                            currency: item.currency,
                            hideCurrency: true,
                          })
                        }}<span class="text-blue text-caption ms-1">{{
                          item.currency.toUpperCase()
                        }}</span>
                      </template>
                    </i18n-t>
                  </v-chip>
                  <v-btn
                    v-else
                    variant="tonal"
                    size="small"
                    class="text-none"
                    color="blue"
                    @click="handleItemDiscountClick(item)"
                  >
                    <v-icon size="small" class="mr-1">mdi-sale-outline</v-icon>
                    {{ $t("common.cart.discount") }}
                  </v-btn>
                </v-col>
              </v-row>

              <v-row no-gutters align="center" class="ga-2 mt-2">
                <v-col cols="auto">
                  <v-row no-gutters align="center">
                    <v-col cols="auto">
                      <v-btn
                        :icon="getDecreaseButtonIcon(item.quantity)"
                        variant="tonal"
                        size="small"
                        class="quantity-adjust-button"
                        :color="item.quantity <= 1 ? 'error' : 'primary'"
                        @click="decreaseQuantity(item.id, item.quantity)"
                      />
                    </v-col>
                    <v-col cols="auto" class="mx-4 pa-0">
                      <v-sheet width="32" color="transparent" class="text-center">
                        <span
                          :class="{
                            'text-warning':
                              item.hasInventory !== false &&
                              cartStore.isItemInOversellTerritory(item),
                            'text-error':
                              item.hasInventory !== false &&
                              cartStore.isItemOverOversellLimit(item),
                          }"
                          class="text-caption font-weight-medium"
                        >
                          {{ item.quantity }}
                        </span>
                      </v-sheet>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        icon="mdi-plus"
                        variant="tonal"
                        size="small"
                        class="quantity-adjust-button"
                        color="primary"
                        :disabled="cartStore.isItemAtMaxStock(item)"
                        @click="increaseQuantity(item.id)"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-list-item>
      </v-slide-x-reverse-transition>

      <v-divider class="mt-2 mb-2"></v-divider>

      <!-- Checkout Warning Message -->
      <v-alert
        v-if="hasInventoryIssues"
        type="error"
        variant="tonal"
        density="compact"
        class="mx-4 mb-2"
      >
        {{ inventoryIssueMessage }}
      </v-alert>

      <!-- Checkout Section -->
      <div class="pt-2 pb-4 px-4">
        <div class="d-flex flex-wrap align-center ga-2 mb-4">
          <v-btn
            variant="tonal"
            color="blue"
            prepend-icon="mdi-sale"
            class="text-none"
            :disabled="cartDiscountDisabled"
            @click="handleCartDiscountClick"
          >
            {{ $t("common.cart.cartDiscount") }}
          </v-btn>
          <v-spacer class="mx-2" />
          <v-btn
            variant="text"
            color="error"
            prepend-icon="mdi-close-circle-multiple-outline"
            class="text-none"
            :disabled="!cartStore.hasAnyDiscounts"
            @click="cartStore.clearAllDiscounts()"
          >
            {{ $t("common.cart.clearDiscounts") }}
          </v-btn>
        </div>

        <v-card variant="outlined" class="cart-totals-card pa-4 mb-4">
          <v-row v-if="discountTotal > 0" no-gutters align="center" class="mb-1">
            <v-col class="text-body-2">{{ $t("common.cart.originalSubtotal") }}</v-col>
            <v-col cols="auto" class="text-end">
              <span class="text-body-2 text-decoration-line-through text-error">{{
                formatMoney({
                  amount: preDiscountSubtotal,
                  currency: cartCurrency,
                  hideCurrency: true,
                })
              }}</span>
              <small class="text-grey ms-1">{{ cartCurrency.toUpperCase() }}</small>
            </v-col>
          </v-row>
          <v-row v-if="discountTotal > 0" no-gutters align="center" class="mb-1">
            <v-col class="text-body-2">{{ $t("common.cart.discountsApplied") }}</v-col>
            <v-col cols="auto" class="text-end">
              <span class="text-body-2 text-success">{{
                formatMoney({
                  amount: discountTotal * -1,
                  currency: cartCurrency,
                  hideCurrency: true,
                })
              }}</span>
              <small class="text-grey ms-1">{{ cartCurrency.toUpperCase() }}</small>
            </v-col>
          </v-row>
          <v-divider class="my-3" v-if="discountTotal > 0" />
          <v-row no-gutters align="center">
            <v-col class="text-subtitle-1 font-weight-medium">{{
              $t("common.action.subTotal")
            }}</v-col>
            <v-col cols="auto" class="text-end">
              <span class="text-h5 font-weight-bold">{{
                formatMoney({ amount: cartTotal, currency: cartCurrency, hideCurrency: true })
              }}</span>
              <small class="text-grey ms-1">{{ cartCurrency.toUpperCase() }}</small>
            </v-col>
          </v-row>
          <div v-if="isTaxesEnabled" class="text-caption text-grey-darken-1 mt-1">
            <small>{{ $t("common.cart.taxCartNote", { location: appBarLocationName }) }}</small>
          </div>
        </v-card>

        <v-btn
          prepend-icon="mdi-shield-lock"
          block
          size="large"
          color="primary"
          variant="flat"
          class="mb-2"
          @click="checkout"
          :loading="cartStore.loading"
          :disabled="cartStore.loading || hasInventoryIssues"
        >
          {{ $t("common.cart.checkout") }}
        </v-btn>
      </div>
    </v-list>

    <div v-else class="d-flex flex-column align-center justify-center fill-height">
      <v-icon icon="mdi-cart-outline" size="64" color="grey-lighten-1" class="mb-4"></v-icon>
      <span class="text-subtitle-1 text-grey">{{ $t("common.cart.noItems") }}</span>
    </div>
  </v-navigation-drawer>

  <CartDiscountModal
    v-model="discountModal.visible"
    :scope="discountModal.scope"
    :item-id="discountModal.itemId"
  />
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useCartStore } from "@/cart/stores/cart";
import default_product_image from "@/shared/assets/default_product_image.png";
import { formatMoney } from "@/shared/services/util.service";
import {
  getCartItemImage,
  getCartItemPrimaryLabel,
  getCartItemProductName,
  getCartQuantity,
} from "@/cart/utils/cart.utils";
import { useRouter, useRoute } from "vue-router";
import { PlatformFeature } from "@/shared/enums/feature.enum";
import { Pages } from "@/shared/enums/route.enum";
import { useDisplay } from "vuetify";
import { isTaxesEnabled } from "@/shared/utils/tax.utils";
import CartDiscountModal from "@/cart/components/cart/CartDiscountModal.vue";
import type { CartItem, DiscountScope } from "@/cart/types/cart";
import {
  getItemNetSubtotal,
  getItemNetUnitAmount,
  hasDiscountApplied,
} from "@/shared/utils/discount.utils";
import { useLocationStore } from "@/terminals/stores/locations";
import i18n from "@/app/plugins/i18n";

const cartStore = useCartStore();
const cartItems = computed(() => cartStore.items);
const cartTotal = computed(() => cartStore.total);
const preDiscountSubtotal = computed(() => cartStore.preDiscountSubtotal);
const discountTotal = computed(() => cartStore.discountTotal);
const cartCurrency = computed(() => cartItems.value[0]?.currency || "usd");
const { mobile } = useDisplay();

const router = useRouter();
const route = useRoute();
const locationStore = useLocationStore();

const locations = computed(() => locationStore.globalLocations?.locations ?? []);

const appBarLocationName = computed(() => {
  const selected = locationStore.selectedLocation;
  return selected?.display_name ?? selected?.id ?? "Unknown";
});

const getLocationName = (locationId: string) => {
  const match = locations.value.find((location) => location.id === locationId);
  return match?.display_name ?? locationId;
};

const getLocationSuffix = (item: CartItem) => {
  if (!item.locationId) {
    return "";
  }
  return i18n.global.t("common.cart.atLocation", { location: getLocationName(item.locationId) });
};

interface DiscountModalState {
  visible: boolean;
  scope: DiscountScope;
  itemId: string | null;
}

const discountModal = ref<DiscountModalState>({
  visible: false,
  scope: "item",
  itemId: null,
});

const openDiscountModal = (scope: DiscountScope, itemId: string | null = null) => {
  discountModal.value = {
    visible: true,
    scope,
    itemId,
  };
};

const handleItemDiscountClick = (item: CartItem) => {
  openDiscountModal("item", item.id);
};

const handleCartDiscountClick = () => {
  openDiscountModal("cart", null);
};

const itemHasDiscount = (item: CartItem) => hasDiscountApplied(item);

const getNetUnitPrice = (item: CartItem) => getItemNetUnitAmount(item);
const getNetLineTotal = (item: CartItem) => getItemNetSubtotal(item);
const getOriginalLineTotal = (item: CartItem) => item.unitAmount * item.quantity;

const formatUnitPrice = (amount: number, currency: string) => {
  return formatMoney({ amount, currency, hideCurrency: true });
};

const cartDiscountDisabled = computed(() => {
  return cartItems.value.length === 0;
});

const isScannerPage = computed(() => {
  return route.name === Pages.SCANNER;
});

const isMobile = computed(() => {
  return mobile.value;
});

// Watch for route changes to handle drawer visibility
watch(
  () => route.name,
  (newRoute) => {
    cartStore.isVisible = newRoute === Pages.SCANNER && !isMobile.value;
  },
  { immediate: true },
);

onMounted(() => {
  // Set initial visibility based on current route and screen size
  if (route.name === Pages.SCANNER && !isMobile.value) {
    cartStore.isVisible = true;
  }
});

// Check for inventory issues
const getInventoryLowerBound = (item: CartItem): number | null =>
  cartStore.getInventoryLowerBound(item);

const getOversellLimitLabel = (item: CartItem): string => cartStore.getOversellLimitLabel(item);

const getProjectedStockCount = (item: CartItem): number =>
  (item.stockCount || 0) -
  cartStore.getInventoryBucketQuantity(item.productId, item.locationId ?? null);

const hasInventoryIssues = computed(() => {
  return cartItems.value.some(
    (item) => item.hasInventory === true && cartStore.isItemOverOversellLimit(item),
  );
});

// Generate an appropriate error message based on inventory issues
const inventoryIssueMessage = computed(() => {
  const outOfStockItems = cartItems.value.filter(
    (item) =>
      item.hasInventory === true &&
      getInventoryLowerBound(item) !== null &&
      (item.stockCount || 0) <= getInventoryLowerBound(item)! &&
      cartStore.isItemOverOversellLimit(item),
  );
  const overLimitItems = cartItems.value.filter(
    (item) => item.hasInventory === true && cartStore.isItemOverOversellLimit(item),
  );

  if (outOfStockItems.length > 0) {
    return outOfStockItems.length === 1
      ? i18n.global.t("common.cart.itemOutOfStock", {
          item:
            getCartItemProductName(outOfStockItems[0]) ||
            getCartItemPrimaryLabel(outOfStockItems[0]),
        })
      : i18n.global.t("common.cart.itemsOutOfStock", { count: outOfStockItems.length });
  }

  if (overLimitItems.length > 0) {
    return overLimitItems.length === 1
      ? i18n.global.t("common.cart.itemExceedsOversellLimit", {
          item:
            getCartItemProductName(overLimitItems[0]) || getCartItemPrimaryLabel(overLimitItems[0]),
          limit: getOversellLimitLabel(overLimitItems[0]),
        })
      : i18n.global.t("common.cart.itemsExceedOversellLimits", { count: overLimitItems.length });
  }

  return i18n.global.t("common.cart.inventoryIssuesPreventCheckout");
});

// Deprecated - Currently not being used keep this here for now just incase it is needed again
// // Add product to cart with a specific price
// const addToCart = (productId: string, priceId: string) => {
//   if (productId && priceId) {
//     // Check if the new method exists, otherwise create a workaround
//     if (typeof cartStore.addProductWithPrice === 'function') {
//       cartStore.addProductWithPrice(productId, priceId);
//     } else {
//       // Fallback implementation - try to fetch the product and price directly
//       console.warn('addProductWithPrice not available, using fallback implementation');
//       const dummyItem = {
//         id: priceId,
//         product: productId
//       };
//       cartStore.addItem(dummyItem);
//     }
//   }
// };

const increaseQuantity = (itemId: string) => {
  cartStore.updateItemQuantity(itemId, 1);
};

const getDecreaseButtonIcon = (quantity: number): string =>
  quantity <= 1 ? "mdi-trash-can-outline" : "mdi-minus";

const decreaseQuantity = (itemId: string, quantity: number) => {
  if (quantity <= 1) {
    cartStore.removeItem(itemId);
    return;
  }

  cartStore.updateItemQuantity(itemId, -1);
};

const checkout = async () => {
  if (!hasInventoryIssues.value) {
    await cartStore.checkoutCart();
  }
};

const redirectToScannerPage = () => {
  router.push({ name: Pages.SCANNER });
};
</script>

<style scoped>
.cart-totals-card {
  border-color: #e0e0e0 !important;
}

.quantity-adjust-button {
  padding-inline: 0 !important;
}

.quantity-adjust-button :deep(.v-icon) {
  font-size: 16px;
}
</style>
